import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { formatSparklineTooltip, pickLongestSparklineSeries } from './parseSparklineValues';
import {
  buildSparklinePlot,
  clientXToSvgX,
  getSparklineIndexAtSvgX,
  type SparklinePlotPoint,
} from './sparklineGeometry';

const SPARKLINE_LINE_COLOR = 'var(--table-sparkline-line-color, #7cb5ec)';

const CHART_HEIGHT = 54;

const Root = styled.div`
  height: 62px;
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow: visible;
`;

const SvgContainer = styled.svg`
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: visible;
`;

const TooltipBubble = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  padding: 5px 8px;
  background: #fff;
  color: rgba(0, 0, 0, 0.87);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  font-size: 11px;
  font-weight: 600;
  border-radius: 2px;
  transform: translate(10px, -30px);
  white-space: nowrap;
`;

type SparklineChartProps = {
  data?: { data?: unknown };
  width?: number;
  hasTooltip?: boolean;
};

export default function SparklineChart({ data, width: widthProp = 0, hasTooltip = true }: SparklineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const values = useMemo(() => pickLongestSparklineSeries(data?.data), [data?.data]);
  const plotWidth = measuredWidth > 0 ? measuredWidth : widthProp;
  const plot = useMemo(
    () => (plotWidth > 0 ? buildSparklinePlot(values, plotWidth, CHART_HEIGHT) : null),
    [values, plotWidth],
  );
  const [activePoint, setActivePoint] = useState<{
    point: SparklinePlotPoint;
    clientX: number;
    clientY: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      const nextWidth = element.getBoundingClientRect().width;
      if (nextWidth > 0) {
        setMeasuredWidth(current => (Math.abs(current - nextWidth) < 0.5 ? current : nextWidth));
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!hasTooltip || !plot || !values.length || plotWidth <= 0) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const svgX = clientXToSvgX(event.clientX, rect, plotWidth);
      const index = getSparklineIndexAtSvgX(values.length, plotWidth, svgX);
      const point = plot.points[index];

      if (!point || point.value === null || point.y === null) {
        setActivePoint(null);
        return;
      }

      setActivePoint({ point, clientX: event.clientX, clientY: event.clientY });
    },
    [hasTooltip, plot, plotWidth, values.length],
  );

  const handleMouseLeave = useCallback(() => {
    setActivePoint(null);
  }, []);

  const tooltip =
    activePoint &&
    createPortal(
      <TooltipBubble style={{ left: activePoint.clientX, top: activePoint.clientY }}>
        {formatSparklineTooltip(activePoint.point.value as number)}
      </TooltipBubble>,
      document.body,
    );

  return (
    <Root ref={containerRef}>
      {tooltip}
      {plot?.segments.length ? (
        <SvgContainer viewBox={`0 0 ${plotWidth} ${CHART_HEIGHT}`} preserveAspectRatio="none" aria-hidden={!hasTooltip}>
          {plot.segments.map((segment, index) => (
            <polyline
              key={index}
              fill="none"
              stroke={SPARKLINE_LINE_COLOR}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={segment.points}
            />
          ))}
          {activePoint?.point.y !== null && activePoint?.point.y !== undefined && (
            <circle cx={activePoint.point.x} cy={activePoint.point.y} r={2.5} fill={SPARKLINE_LINE_COLOR} />
          )}
          <rect
            x={0}
            y={0}
            width={plotWidth}
            height={CHART_HEIGHT}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </SvgContainer>
      ) : null}
    </Root>
  );
}
